import { flexRender } from '@tanstack/react-table';
import { ChevronDown, ChevronUp } from 'lucide-react';
import React from 'react';
import { PaginationControls } from '../PaginationControls';
import { SearchInput } from '../SearchInput';
import { useDataTable } from './hooks/useDataTable';
import type { DataTableProps } from './types';

/**
 * Universal DataTable component with built-in pagination, search, sorting, and selection
 */
export function DataTable<TData>({
  data,
  columns,
  pagination,
  paginationConfig = {
    enabled: true,
    pageSizeOptions: [10, 20, 50],
    defaultPageSize: 20,
    showPageSizeSelector: true,
  },
  onPageChange,
  onPageSizeChange,
  searchConfig = {
    enabled: true,
    placeholder: 'Search...',
    debounceMs: 300,
  },
  onSearch,
  searchQuery = '',
  sorting,
  onSortChange,
  enableSelection = false,
  bulkActions = [],
  onSelectionChange,
  selectedItems = [],
  loadingConfig = {
    loading: false,
    message: 'Loading...',
    showSkeleton: true,
    skeletonRows: 5,
  },
  error,
  emptyStateConfig = {
    title: 'No data found',
    description: 'There are no items to display.',
  },
  className = '',
  bordered = true,
  striped = false,
  size = 'md',
  headerActions,
  title,
  description,
  onRowClick,
  getRowId,
  getRowClassName,
}: DataTableProps<TData>) {

  // Use the data table hook for core functionality
  const {
    table,
    selectedItems: internalSelectedItems,
    selection,
    search,
    bulkActions: bulkActionHelpers,
  } = useDataTable({
    data,
    columns,
    getRowId,
    enableSelection,
    bulkActions,
    initialSorting: sorting || [],
    onSortChange,
    manualSorting: !!onSortChange,
    manualPagination: !!pagination,
  });

  // Handle external selection changes
  React.useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(internalSelectedItems);
    }
  }, [internalSelectedItems, onSelectionChange]);

  // Size classes
  const sizeClasses = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
  };

  // Cell padding classes
  const cellPaddingClasses = {
    sm: 'px-3 py-2',
    md: 'px-6 py-4',
    lg: 'px-8 py-6',
  };

  // Selection column for checkboxes
  const selectionColumn = enableSelection ? {
    id: 'select',
    header: ({ table }: any) => (
      <input
        type="checkbox"
        checked={table.getIsAllPageRowsSelected()}
        {...(table.getIsSomePageRowsSelected() ? { 'data-indeterminate': true } : {})}
        onChange={table.getToggleAllPageRowsSelectedHandler()}
        className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
      />
    ),
    cell: ({ row }: any) => (
      <input
        type="checkbox"
        checked={row.getIsSelected()}
        disabled={!row.getCanSelect()}
        {...(row.getIsSomeSelected() ? { 'data-indeterminate': true } : {})}
        onChange={row.getToggleSelectedHandler()}
        className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
      />
    ),
    enableSorting: false,
    enableColumnFilter: false,
    size: 40,
  } : null;

  // Prepare final columns
  const finalColumns = React.useMemo(() => {
    const cols = [...columns];
    if (selectionColumn) {
      cols.unshift(selectionColumn);
    }
    return cols;
  }, [columns, selectionColumn]);

  // Create table with final columns
  const finalTable = useDataTable({
    data,
    columns: finalColumns,
    getRowId,
    enableSelection,
    bulkActions,
    initialSorting: sorting || [],
    onSortChange,
    manualSorting: !!onSortChange,
    manualPagination: !!pagination,
  }).table;

  // Error state
  if (error) {
    return (
      <div className={`w-full space-y-6 ${className}`}>
        <div className="flex flex-col justify-center items-center py-12">
          <div className="mb-4 text-red-600 dark:text-red-400">
            Error: {error}
          </div>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 text-white bg-blue-600 rounded-md transition-colors hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`w-full space-y-6 ${className}`}>
      {/* Header */}
      {(title || description || headerActions || searchConfig.enabled) && (
        <div className="flex flex-col gap-4 justify-between items-start p-4 bg-gray-50 rounded-lg border border-gray-200 sm:flex-row sm:items-center dark:bg-gray-800 dark:border-gray-700">
          <div className="flex-1">
            {title && (
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {title}
              </h2>
            )}
            {description && (
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {description}
              </p>
            )}
          </div>
          
          <div className="flex flex-col gap-4 items-start sm:flex-row sm:items-center">
            {searchConfig.enabled && (
              <SearchInput
                onSearch={onSearch || search.query}
                placeholder={searchConfig.placeholder}
                className="w-full sm:w-auto sm:min-w-[300px]"
              />
            )}
            
            {headerActions && (
              <div className="flex gap-2 items-center">
                {headerActions}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bulk Actions Bar */}
      {enableSelection && internalSelectedItems.length > 0 && bulkActions.length > 0 && (
        <div className="flex gap-4 justify-between items-center p-4 bg-blue-50 rounded-lg border border-blue-200 dark:bg-blue-900/20 dark:border-blue-800">
          <div className="text-sm text-blue-800 dark:text-blue-200">
            {internalSelectedItems.length} item{internalSelectedItems.length !== 1 ? 's' : ''} selected
          </div>
          <div className="flex gap-2">
            {bulkActions.map((action) => (
              <button
                key={action.id}
                onClick={() => bulkActionHelpers.execute(action.id)}
                disabled={!bulkActionHelpers.canExecute(action.id)}
                className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                  action.destructive
                    ? 'text-red-700 bg-red-100 hover:bg-red-200 dark:text-red-400 dark:bg-red-900/30 dark:hover:bg-red-900/50'
                    : 'text-blue-700 bg-blue-100 hover:bg-blue-200 dark:text-blue-400 dark:bg-blue-900/30 dark:hover:bg-blue-900/50'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {action.icon && <span className="mr-1">{action.icon}</span>}
                {action.label}
              </button>
            ))}
            <button
              onClick={selection.clearSelection}
              className="px-3 py-1 text-sm font-medium text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loadingConfig.loading && data.length === 0 && (
        <div className="flex justify-center items-center py-12">
          <div className="flex gap-2 items-center">
            <div className="w-6 h-6 rounded-full border-2 border-blue-600 animate-spin border-t-transparent"></div>
            <span className="text-gray-600 dark:text-gray-400">{loadingConfig.message}</span>
          </div>
        </div>
      )}

      {/* Table */}
      {!loadingConfig.loading || data.length > 0 ? (
        <div className={`w-full overflow-x-auto ${bordered ? 'rounded-lg border border-gray-200 dark:border-gray-700' : ''}`}>
          <table className="w-full min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              {finalTable.getHeaderGroups().map((headerGroup: any) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header: any) => (
                    <th
                      key={header.id}
                      className={`${cellPaddingClasses[size]} text-xs font-medium tracking-wider text-left text-gray-500 uppercase dark:text-gray-400 ${
                        header.column.getCanSort() ? 'cursor-pointer select-none hover:bg-gray-100 dark:hover:bg-gray-700' : ''
                      }`}
                      onClick={header.column.getCanSort() ? header.column.getToggleSortingHandler() : undefined}
                      style={{ width: (header.column.columnDef as any).width }}
                    >
                      {header.isPlaceholder ? null : (
                        <div className="flex gap-2 items-center">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.getCanSort() && (
                            <div className="flex flex-col">
                              {header.column.getIsSorted() === 'desc' ? (
                                <ChevronDown className="w-4 h-4" />
                              ) : header.column.getIsSorted() === 'asc' ? (
                                <ChevronUp className="w-4 h-4" />
                              ) : (
                                <div className="w-4 h-4 opacity-30">
                                  <ChevronUp className="w-4 h-4" />
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className={`bg-white divide-y divide-gray-200 dark:bg-gray-900 dark:divide-gray-700 ${sizeClasses[size]}`}>
              {finalTable.getRowModel().rows.map((row: any) => (
                <tr
                  key={row.id}
                  className={`transition-colors ${
                    striped && row.index % 2 === 1 ? 'bg-gray-50 dark:bg-gray-800/50' : ''
                  } hover:bg-gray-50 dark:hover:bg-gray-800 ${
                    onRowClick ? 'cursor-pointer' : ''
                  } ${getRowClassName ? getRowClassName(row.original) : ''}`}
                  onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                >
                  {row.getVisibleCells().map((cell: any) => (
                    <td key={cell.id} className={`${cellPaddingClasses[size]} whitespace-nowrap`}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {/* Empty State */}
      {!loadingConfig.loading && data.length === 0 && (
        <div className="py-12 text-center">
          <div className="mb-4">
            {emptyStateConfig.icon}
          </div>
          <div className="mb-4 text-gray-500 dark:text-gray-400">
            <h3 className="text-lg font-medium mb-2">{emptyStateConfig.title}</h3>
            <p>{emptyStateConfig.description}</p>
          </div>
          {emptyStateConfig.action && (
            <div>{emptyStateConfig.action}</div>
          )}
        </div>
      )}

      {/* Pagination */}
      {pagination && paginationConfig.enabled && (
        <PaginationControls
          pagination={pagination}
          onPageChange={onPageChange || (() => {})}
          onNextPage={() => onPageChange && onPageChange(pagination.page + 1)}
          onPreviousPage={() => onPageChange && onPageChange(pagination.page - 1)}
          loading={loadingConfig.loading}
          pageSizeOptions={paginationConfig.pageSizeOptions}
          onPageSizeChange={onPageSizeChange}
          showPageSizeSelector={paginationConfig.showPageSizeSelector}
        />
      )}

      {/* Loading indicator for page changes */}
      {loadingConfig.loading && data.length > 0 && (
        <div className="fixed top-0 right-0 left-0 z-50 py-2 bg-blue-100 border-b border-blue-200 dark:bg-blue-900 dark:border-blue-800">
          <div className="flex gap-2 justify-center items-center">
            <div className="w-4 h-4 rounded-full border-2 border-blue-600 animate-spin border-t-transparent"></div>
            <span className="text-sm text-blue-800 dark:text-blue-200">Loading...</span>
          </div>
        </div>
      )}
    </div>
  );
} 